import React, { useRef, useState } from "react";
import { FaRegEyeSlash } from "react-icons/fa";
import { FaRegEye } from "react-icons/fa6";
import awaken from "../assets/awaken.png";
import NavBar from "./navBar";
import Loader from "./reusableComponents/loader";
import { useStores } from "../stores";
import adminAuthService from "../services/authService";

const Login = () => {
	const { clientsStore } = useStores();
	const [open, setOpen] = useState("password");
	const [error, setError] = useState("");
	const passwordRef = useRef(null);
	const [errorData, setErrorData] = useState({
		errorEmail: "",
		errorPassword: "",
	});
	const showPassword = () => {
		setOpen(open === "password" ? "text" : "password");
	};
	const [userData, setUserData] = useState({
		email: "",
		password: "",
	});
	const [load, setLoad] = useState(false);

	// Forgot password flow
	const [forgotOpen, setForgotOpen] = useState(false);
	const [forgotStep, setForgotStep] = useState("email"); // "email" | "otp"
	const [forgotEmail, setForgotEmail] = useState("");
	const [forgotOtp, setForgotOtp] = useState("");
	const [forgotNewPassword, setForgotNewPassword] = useState("");
	const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
	const [forgotError, setForgotError] = useState("");
	const [forgotLoad, setForgotLoad] = useState(false);
	const [forgotSuccess, setForgotSuccess] = useState("");

	const openForgotModal = () => {
		setForgotOpen(true);
		setForgotStep("email");
		setForgotEmail("");
		setForgotOtp("");
		setForgotNewPassword("");
		setForgotConfirmPassword("");
		setForgotError("");
		setForgotSuccess("");
		setErrorData({ errorEmail: "", errorPassword: "" });
		setError("");
	};

	const closeForgotModal = () => {
		setForgotOpen(false);
		setForgotStep("email");
		setForgotEmail("");
		setForgotOtp("");
		setForgotNewPassword("");
		setForgotConfirmPassword("");
		setForgotError("");
		setForgotSuccess("");
		setForgotLoad(false);
	};

	const handleForgotRequestOtp = async (e) => {
		e.preventDefault();
		const email = forgotEmail.trim();
		if (!email) {
			setForgotError("Please enter your email.");
			return;
		}
		setForgotLoad(true);
		setForgotError("");
		const result = await adminAuthService.forgotPassword(email);
		setForgotLoad(false);
		if (result.success) {
			setForgotStep("otp");
			setForgotSuccess(result.message || "Check your email for the OTP.");
			setForgotError("");
		} else {
			setForgotError(result.error || "Failed to send OTP.");
		}
	};

	const handleForgotResetPassword = async (e) => {
		e.preventDefault();
		setForgotError("");
		if (!forgotOtp.trim()) {
			setForgotError("Please enter the OTP from your email.");
			return;
		}
		if (!forgotNewPassword) {
			setForgotError("Please enter a new password.");
			return;
		}
		if (forgotNewPassword.length < 6) {
			setForgotError("Password must be at least 6 characters.");
			return;
		}
		if (forgotNewPassword !== forgotConfirmPassword) {
			setForgotError("Passwords do not match.");
			return;
		}
		setForgotLoad(true);
		const result = await adminAuthService.resetPassword({
			email: forgotEmail.trim(),
			otp: forgotOtp.trim(),
			newPassword: forgotNewPassword,
		});
		setForgotLoad(false);
		if (result.success) {
			setForgotSuccess(result.message || "Password updated. You can now log in.");
			setForgotError("");
			setTimeout(() => {
				closeForgotModal();
			}, 2000);
		} else {
			setForgotError(result.error || "Failed to reset password.");
		}
	};

	const handleSubmit = async (data) => {
		if (data.email !== "" && data.password !== "") {
			setLoad(true);
			setError(""); // Clear any previous errors
			try {
				console.log('Attempting login with:', userData); // Debug log
				const result = await adminAuthService.login(userData);
				console.log('Login result:', result); // Debug log
				
				if (result.success) {
					// Set user data in store
					clientsStore.setUserData(result.user);
					console.log('User data set in store:', result.user); // Debug log
					
					// Navigate to dashboard
					window.location.href = "/dashboard/all-awakens";
				} else {
					console.error("Login failed:", result.error);
					setError(result.error);
					setLoad(false);
				}
			} catch (error) {
				console.error("Error during login:", error);
				setError("Login failed. Please try again.");
				setLoad(false);
			}
		} else {
			setErrorData({
				errorEmail: data.email === "" ? "Email is required" : "",
				errorPassword: data.password === "" ? "Password is required" : "",
			});
		}
	};

	return (
		<>
			<NavBar />
			<div className="flex">
				<div className="w-[55%]">
					<img src={awaken} alt="Login" className="w-[44vw] h-[37vw]" />
				</div>
				<div className="w-[45%] mt-[9%] space-y-4">
					<h1 className="font-bold text-3xl">Enter details to login</h1>
					{/* display error messages here */}
					<p className="text-red-500">{error}</p>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit(userData);
						}}
					>
						<div className="flex flex-col relative">
							<div className="relative">
								<input
									type="email"
									id="email"
									name="email"
									value={userData.email}
									className={`border border-gray-300 rounded-lg p-3 ${
										errorData.errorEmail === "" && "mb-4"
									} w-[60%] px-6 peer focus:outline-primary`}
									autoComplete="username"
									onInput={(e) =>
										e.target.value
											? e.target.classList.add("has-value")
											: e.target.classList.remove("has-value")
									}
									onChange={(e) => {
										setUserData({ ...userData, email: e.target.value });
										setErrorData({
											errorEmail: "",
											errorPassword: "",
										});
										setError("");
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											passwordRef.current.focus(); // Focus the password input
										}
									}}
								/>
								<label
									htmlFor="email"
									className="absolute top-3 left-5 transition-all duration-200 ease-in-out peer-focus:-top-3 peer-focus:text-primary bg-white px-2"
								>
									Email
								</label>
							</div>
							{errorData.errorEmail && (
								<p className="text-red-500 px-6 top-6">
									{errorData.errorEmail}
								</p>
							)}
							<div className="relative">
								<input
									type={open}
									id="password"
									name="password"
									value={userData.password}
									className={`border border-gray-300 rounded-lg p-3 px-6 peer ${
										errorData.errorEmail === "" && "mb-4"
									} w-[60%] focus:outline-primary`}
									autoComplete="current-password"
									onInput={(e) =>
										e.target.value
											? e.target.classList.add("has-value")
											: e.target.classList.remove("has-value")
									}
									onChange={(e) => {
										setUserData({ ...userData, password: e.target.value });
										setErrorData({
											errorEmail: "",
											errorPassword: "",
										});
										setError("");
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											handleSubmit(userData);
										}
									}}
									ref={passwordRef}
								/>
								<label
									htmlFor="password"
									className="absolute top-3 left-5 transition-all duration-200 ease-in-out peer-focus:-top-3 peer-focus:text-primary bg-white px-2"
								>
									Password
								</label>
								<button
									onClick={(e) => {
										e.preventDefault();
										showPassword();
									}}
								>
									{open === "password" ? (
										<FaRegEyeSlash className="absolute text-primary top-[25%] right-[43%] z-20" />
									) : (
										<FaRegEye className="absolute text-primary top-[25%] right-[43%] z-20" />
									)}
								</button>
							</div>
							{errorData.errorPassword && (
								<p className="text-red-500 px-6 top-6">
									{errorData.errorPassword}
								</p>
							)}
							<button
								type="button"
								className="text-primary text-sm"
								onClick={(e) => {
									e.preventDefault();
									openForgotModal();
								}}
							>
								Forgot Password?
							</button>
						</div>
						{/* <div className='flex items-center'> */}
						<button
							type="submit"
							className="bg-primary text-white px-6 py-2 my-5 rounded-lg text-md active:bg-blue-600 ml-[40%]"
						>
							Login Now
						</button>
						{/* </div> */}
					</form>
				</div>
			</div>
			<Loader
				loader={load}
				text={"Please wait we are validating your credentials"}
			/>

			{/* Forgot Password modal */}
			{forgotOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => e.target === e.currentTarget && closeForgotModal()}>
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
						<div className="flex justify-between items-center mb-4">
							<h2 className="font-bold text-xl text-primary">Forgot Password</h2>
							<button type="button" className="text-gray-500 hover:text-gray-700 text-2xl leading-none" onClick={closeForgotModal} aria-label="Close">&times;</button>
						</div>
						{forgotSuccess && <p className="text-green-600 text-sm mb-3">{forgotSuccess}</p>}
						{forgotError && <p className="text-red-500 text-sm mb-3">{forgotError}</p>}

						{forgotStep === "email" && (
							<form onSubmit={handleForgotRequestOtp}>
								<label className="block text-sm font-medium text-gray-700 mb-1">Admin email</label>
								<input
									type="email"
									value={forgotEmail}
									onChange={(e) => { setForgotEmail(e.target.value); setForgotError(""); }}
									className="border border-gray-300 rounded-lg p-3 w-full mb-4 focus:outline-primary"
									placeholder="admin@example.com"
									autoComplete="email"
									disabled={forgotLoad}
								/>
								<div className="flex gap-2 justify-end">
									<button type="button" className="px-4 py-2 rounded-lg border border-gray-300" onClick={closeForgotModal}>Cancel</button>
									<button type="submit" className="bg-primary text-white px-4 py-2 rounded-lg" disabled={forgotLoad}>
										{forgotLoad ? "Sending…" : "Send OTP"}
									</button>
								</div>
							</form>
						)}

						{forgotStep === "otp" && (
							<form onSubmit={handleForgotResetPassword}>
								<div className="mb-3">
									<label className="block text-sm font-medium text-gray-700 mb-1">OTP (from email)</label>
									<input
										type="text"
										inputMode="numeric"
										maxLength={6}
										value={forgotOtp}
										onChange={(e) => { setForgotOtp(e.target.value.replace(/\D/g, "")); setForgotError(""); }}
										className="border border-gray-300 rounded-lg p-3 w-full focus:outline-primary"
										placeholder="123456"
										disabled={forgotLoad}
									/>
								</div>
								<div className="mb-3">
									<label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
									<input
										type="password"
										value={forgotNewPassword}
										onChange={(e) => { setForgotNewPassword(e.target.value); setForgotError(""); }}
										className="border border-gray-300 rounded-lg p-3 w-full focus:outline-primary"
										placeholder="At least 6 characters"
										autoComplete="new-password"
										disabled={forgotLoad}
									/>
								</div>
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
									<input
										type="password"
										value={forgotConfirmPassword}
										onChange={(e) => { setForgotConfirmPassword(e.target.value); setForgotError(""); }}
										className="border border-gray-300 rounded-lg p-3 w-full focus:outline-primary"
										placeholder="Repeat new password"
										autoComplete="new-password"
										disabled={forgotLoad}
									/>
								</div>
								<div className="flex gap-2 justify-end">
									<button type="button" className="px-4 py-2 rounded-lg border border-gray-300" onClick={() => { setForgotStep("email"); setForgotError(""); }} disabled={forgotLoad}>Back</button>
									<button type="submit" className="bg-primary text-white px-4 py-2 rounded-lg" disabled={forgotLoad}>
										{forgotLoad ? "Updating…" : "Update password"}
									</button>
								</div>
							</form>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default Login;
